import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Skeleton for the live-tracking page. Mirrors the real layout pretty
// closely since this page is usually reached via deep link and the
// booking load is what the user is waiting on:
//   forest hero (eyebrow + current-stage title + booking id line) ·
//   2-col grid (map+trustian+items on the left, status timeline +
//   collection details on the right). Shimmers on forest use
//   translucent whites; the map block already has a mint gradient so
//   a ghost marker reads cleanly there too.

const StageRowShimmer = ({ current = false }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Shimmer
      width={28}
      height={28}
      circular
      sx={{
        bgcolor: current ? 'rgba(61,122,82,0.35)' : 'var(--border-l)',
        boxShadow: current ? '0 0 0 5px rgba(61,122,82,0.2)' : 'none',
      }}
    />
    <Box sx={{ flex: 1 }}>
      <Shimmer width="60%" height={12} rounded={4} sx={{ mb: current ? 0.5 : 0 }} />
      {current && <Shimmer width={90} height={10} rounded={4} />}
    </Box>
  </Stack>
);

const TrackShimmer = () => (
  <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
    {/* Forest hero */}
    <Box sx={{ bgcolor: 'var(--forest)', pt: { xs: 10, md: 12 }, pb: 5 }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1200, px: { xs: 2, md: 4 } }}>
        <Shimmer width={90} height={10} rounded={4} sx={{ bgcolor: 'rgba(200,237,216,0.25)', mb: 1.5 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 1, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Shimmer width={260} height={28} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)', mb: 1 }} />
            <Shimmer width={320} height={11} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
          </Box>
          <Shimmer width={78} height={22} rounded={100} sx={{ bgcolor: 'rgba(134,239,172,0.25)' }} />
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="xl" sx={{ maxWidth: 1200, py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          gap: { xs: 2, md: 3 },
          alignItems: 'start',
        }}
      >
        {/* LEFT: map + trustian + items */}
        <Box>
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid var(--border-l)',
              borderRadius: '20px',
              overflow: 'hidden',
              mb: 2.5,
            }}
          >
            {/* Map */}
            <Box
              sx={{
                height: { xs: 220, md: 300 },
                position: 'relative',
                background: 'linear-gradient(135deg, #E7F5ED 0%, #C8EDD8 50%, #A0D9B8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Shimmer
                width={180}
                height={40}
                rounded={12}
                sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
              />
            </Box>

            {/* Trustian card */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, width: '100%' }}>
                  <Shimmer width={52} height={52} circular />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Shimmer width={130} height={14} rounded={4} sx={{ mb: 0.75 }} />
                    <Shimmer width={200} height={11} rounded={4} />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Shimmer width={78} height={34} rounded={6} />
                  <Shimmer width={78} height={34} rounded={6} />
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* Items card */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: { xs: 2.5, md: 3 }, mb: 2.5 }}>
            <Shimmer width={160} height={18} rounded={6} sx={{ mb: 2 }} />
            <Stack spacing={1.25}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1, borderBottom: i < 2 ? '1px dashed var(--border-l)' : 'none' }}
                >
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Shimmer width="70%" height={13} rounded={4} sx={{ mb: 0.5 }} />
                    <Shimmer width="45%" height={10} rounded={4} />
                  </Box>
                  <Shimmer width={70} height={14} rounded={4} />
                </Stack>
              ))}
            </Stack>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid var(--border-l)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Shimmer width={180} height={11} rounded={4} />
              <Shimmer width={90} height={22} rounded={6} />
            </Box>
          </Box>
        </Box>

        {/* RIGHT: timeline + patient */}
        <Box>
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3, mb: 2.5 }}>
            <Shimmer width={110} height={18} rounded={6} sx={{ mb: 1 }} />
            <Shimmer width={200} height={11} rounded={4} sx={{ mb: 2 }} />
            <Shimmer width="100%" height={6} rounded={10} sx={{ mb: 3 }} />
            <Stack spacing={2.5}>
              {Array.from({ length: 5 }).map((_, i) => (
                <StageRowShimmer key={i} current={i === 1} />
              ))}
            </Stack>
          </Box>

          {/* Collection details */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3 }}>
            <Shimmer width={130} height={10} rounded={4} sx={{ mb: 1.25 }} />
            <Shimmer width={160} height={14} rounded={4} sx={{ mb: 0.75 }} />
            <Shimmer width={220} height={11} rounded={4} sx={{ mb: 1.5 }} />
            <Shimmer width="95%" height={12} rounded={4} sx={{ mb: 0.5 }} />
            <Shimmer width="70%" height={12} rounded={4} />
          </Box>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default TrackShimmer;
